import { useAtom, useAtomValue } from "jotai";
import { allCCTState, cctLoadContextState, lastValidatedWorkingTime } from "../atoms/core";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { FIND_CCT, GET_CCT_BY_EDITION_BY } from "../graphql/cct";
import { useCallback, useEffect, useMemo } from "react";
import { useI18n } from "./i18n-hooks";
import {
    Cct,
    CctFee,
    ClientProfile,
    EarningType,
    FeeType,
    Mission,
    Repetition,
    Unit,
    WorkingTimeClassicType,
} from "../__generated__/graphql";
import { CoreContractFieldsFragment } from "../__generated__/operations/graphql";
import { useCatchLazyQueryError } from "./errors";
import { useCurrentProfile } from "./use-current-profile";
import { getWorkingTimeClassicPercentage } from "../utils";
import { useSetAlert } from "./use-set-alert";

/**
 * Load the CCTs for the given state code in the allCCTState jotai atom.
 * @param stateCode State code i.e. "VS"
 * @param startDate Optional mission start date to filter CCTs by validity date
 */
export const useLoadCCTs = (
    stateCode: string | undefined | null,
    useLse: boolean,
    startDate?: string | null,
) => {
    const [cctLoadContext, setCctLoadContext] = useAtom(cctLoadContextState);
    const [_, setAllCcts] = useAtom(allCCTState);
    const [fetchCcts, { loading }] = useLazyQuery(FIND_CCT, { fetchPolicy: "cache-and-network" });
    const catchLazyQueryError = useCatchLazyQueryError();

    const i18n = useI18n();
    useEffect(() => {
        if (
            stateCode &&
            (cctLoadContext.stateCode !== stateCode ||
                cctLoadContext.locale !== i18n.locale ||
                cctLoadContext.startDate !== (startDate ?? null))
        ) {
            setCctLoadContext({ stateCode, locale: i18n.locale, startDate: startDate ?? null });
            const func = async () => {
                try {
                    const { data, error } = await fetchCcts({
                        variables: {
                            stateCode: stateCode,
                            useLse,
                            limit: 100,
                            startDate: startDate ?? undefined,
                        },
                    });
                    if (error) {
                        throw Error(i18n.t("screens.user.create_mission_cct.cannot_load"));
                    }
                    setAllCcts(data?.findCct?.elements);
                } catch (e: unknown) {
                    catchLazyQueryError(e as Error);
                }
            };
            void func();
        }
    }, [
        stateCode,
        useLse,
        startDate,
        cctLoadContext,
        fetchCcts,
        i18n,
        setAllCcts,
        setCctLoadContext,
        catchLazyQueryError,
    ]);
    return loading;
};

export const useContractCct = (contract?: CoreContractFieldsFragment | null) => {
    const cct = useQuery(GET_CCT_BY_EDITION_BY, {
        variables: {
            cctEditionId: contract?.cctEditionId || "",
        },
        skip: !contract?.cctEditionId,
    });
    return cct?.data?.getCctByEditionId || null;
};

/*
 * Hook to get a function returning the human-readable label for a given cctKey
 */
export const useCctLabel = (useLse: boolean) => {
    const i18n = useI18n();
    const currentProfile = useCurrentProfile<ClientProfile>();
    const { data: cctData } = useQuery(FIND_CCT, {
        fetchPolicy: "cache-and-network",
        variables: {
            useLse,
            limit: 100,
        },
    });

    const allCCT = useMemo(() => cctData?.findCct?.elements || [], [cctData]);

    const getCctLabel = useCallback(
        (cctKey: string): string => {
            if (cctKey === "none") {
                if (currentProfile.client.useLegalEmployer.useLseCct) {
                    return i18n.t("cct_labels.no_cct_lse");
                } else {
                    return i18n.t("cct_labels.no_cct");
                }
            }
            // Find the CCT in allCCT by cctContractId
            const cct = allCCT.find((c) => c.cctContractId === cctKey);
            return cct?.name || cctKey;
        },
        [allCCT, currentProfile.client.useLegalEmployer.useLseCct, i18n],
    );

    return getCctLabel;
};

export const NO_CCT_PREFIX = "lift-no-cct";

export const isNoCct = (cct?: Cct | null) => {
    if (!cct) {
        return false;
    }
    return cct.cctContractId.startsWith(NO_CCT_PREFIX);
};

// Returns the fee configuration the CCT fixes for a given fee type, if any.
export const getCctFeeByType = (
    cct: Cct | null | undefined,
    feeType: FeeType,
): CctFee | undefined => cct?.fees?.find((fee) => (fee.type as FeeType) === feeType);

// This hook return a function that can compute the new hoursPerWeekFullTime and workingTimeClassicSpec to set on the mission contract when user choose a CCT.
// The goal is to adapt mission working tiome and max hours per week when cct change
export const useGetHoursPerWeekAndWorkingTime = () => {
    const { setAlert } = useSetAlert();
    const i18n = useI18n();
    const lastValidatedWorkingTimeValue = useAtomValue(lastValidatedWorkingTime);

    return (mission: Mission, cct: Cct) => {
        let newHoursPerWeekFullTime = lastValidatedWorkingTimeValue?.hoursPerWeekFullTime;
        let newWorkingTimeSpec = lastValidatedWorkingTimeValue?.workingSpec;
        if (newHoursPerWeekFullTime !== undefined) {
            // we cap to the max of the CCT
            const cctMaxHoursPerWeek = cct.hoursPerWeek;
            newHoursPerWeekFullTime = Math.min(newHoursPerWeekFullTime, cctMaxHoursPerWeek);
        }
        if (!newHoursPerWeekFullTime) {
            // keep mission hoursPerWeekFullTime if initial values are not set
            newHoursPerWeekFullTime = mission.currentContract?.hoursPerWeekFullTime || undefined;
        }
        if (!newWorkingTimeSpec) {
            // keep mission workingTimeClassicSpec if initial values are not set
            newWorkingTimeSpec = mission.currentContract
                ?.workingTimeClassicSpec as WorkingTimeClassicType;
        }

        // now try to set the original workingSpec
        if (newHoursPerWeekFullTime && newWorkingTimeSpec) {
            if (mission.currentContract?.earningType === EarningType.Monthly) {
                // for monthly mission we will try to keep work percentage. I know it will be wrong if user did choose a duration
                // but we do not know this information so we choose to keep percentage.
                const originalPercentage = getWorkingTimeClassicPercentage(
                    mission.currentContract?.workingTimeClassicSpec as WorkingTimeClassicType,
                    mission.currentContract?.hoursPerWeekFullTime || 0,
                );
                if (originalPercentage) {
                    // compute new hours per week
                    const newHoursPerWeek = (originalPercentage * newHoursPerWeekFullTime) / 100;
                    newWorkingTimeSpec = {
                        unit: Unit.Hours,
                        repetition: Repetition.Week,
                        unitCount: Math.round(newHoursPerWeek * 100) / 100,
                        dailyHours: null,
                    };
                    if (mission.currentContract?.hoursPerWeekFullTime !== newHoursPerWeekFullTime) {
                        setAlert({
                            title: i18n.t("screens.user.create_mission_cct.hours_adjusted_title"),
                            message: i18n.t(
                                "screens.user.create_mission_cct.hours_adjusted_message",
                                {
                                    oldHours: mission.currentContract?.hoursPerWeekFullTime,
                                    newHours: newHoursPerWeekFullTime,
                                },
                            ),
                        });
                    }
                }
            } else {
                // for non monthly mission we will adapt working time if it is above 100%
                // with new hours per week full time
                const newPercentage = getWorkingTimeClassicPercentage(
                    newWorkingTimeSpec,
                    newHoursPerWeekFullTime,
                );
                if (newPercentage && newPercentage > 100) {
                    const hoursPerDay = newHoursPerWeekFullTime / 5;
                    if (
                        newWorkingTimeSpec.unit === Unit.Days &&
                        newWorkingTimeSpec.repetition === Repetition.Week
                    ) {
                        newWorkingTimeSpec = {
                            ...newWorkingTimeSpec,
                            unitCount: 5,
                            dailyHours: hoursPerDay,
                        };
                    } else if (
                        newWorkingTimeSpec.unit === Unit.Days &&
                        newWorkingTimeSpec.repetition === Repetition.Month
                    ) {
                        newWorkingTimeSpec = {
                            ...newWorkingTimeSpec,
                            unitCount: 20,
                            dailyHours: hoursPerDay,
                        };
                    } else if (
                        newWorkingTimeSpec.unit === Unit.Hours &&
                        newWorkingTimeSpec.repetition === Repetition.Day
                    ) {
                        newWorkingTimeSpec = {
                            ...newWorkingTimeSpec,
                            unitCount: hoursPerDay,
                        };
                    } else if (
                        newWorkingTimeSpec.unit === Unit.Hours &&
                        newWorkingTimeSpec.repetition === Repetition.Week
                    ) {
                        newWorkingTimeSpec = {
                            ...newWorkingTimeSpec,
                            unitCount: newHoursPerWeekFullTime,
                        };
                    } else if (
                        newWorkingTimeSpec.unit === Unit.Hours &&
                        newWorkingTimeSpec.repetition === Repetition.Month
                    ) {
                        newWorkingTimeSpec = {
                            ...newWorkingTimeSpec,
                            unitCount: newHoursPerWeekFullTime * 4,
                        };
                    }
                    setAlert({
                        title: i18n.t("screens.user.create_mission_cct.hours_adjusted_title"),
                        message: i18n.t("screens.user.create_mission_cct.hours_adjusted_message", {
                            oldHours: mission.currentContract?.hoursPerWeekFullTime,
                            newHours: newHoursPerWeekFullTime,
                        }),
                    });
                } else if (newPercentage) {
                    if (cct?.hoursPerWeekDefault) {
                        // we try to see if we can use cct default hours as max to have a better
                        // mission percentage for salary details (impot source, etc...)
                        if (newHoursPerWeekFullTime > cct?.hoursPerWeekDefault) {
                            const percentageWithDefaultHoursPerWeek =
                                getWorkingTimeClassicPercentage(
                                    newWorkingTimeSpec,
                                    cct?.hoursPerWeekDefault,
                                );
                            if (
                                percentageWithDefaultHoursPerWeek &&
                                percentageWithDefaultHoursPerWeek <= 100
                            ) {
                                newHoursPerWeekFullTime = cct?.hoursPerWeekDefault;
                            }
                        }
                    }
                    // in case we use the initial value, check if we need to inform user
                    // we have reset to those initial value
                    if (
                        newHoursPerWeekFullTime >
                        (mission.currentContract?.hoursPerWeekFullTime || 0)
                    ) {
                        setAlert({
                            title: i18n.t("screens.user.create_mission_cct.hours_adjusted_title"),
                            message: i18n.t(
                                "screens.user.create_mission_cct.hours_adjusted_message",
                                {
                                    oldHours: mission.currentContract?.hoursPerWeekFullTime,
                                    newHours: newHoursPerWeekFullTime,
                                },
                            ),
                        });
                    }
                }
            }
        }
        return {
            hoursPerWeekFullTime: newHoursPerWeekFullTime,
            workingTimeClassicSpec: newWorkingTimeSpec,
        };
    };
};
